import React, { useReducer, createContext, useEffect, useState } from "react";
import { TransferActions } from "manifest-components/src/classes/transferInstruction";
import { TransferStatusTypes } from "manifest-components/src/classes/transfer";
import { DevelopmentEnvironmentState } from "./developmentEnvironmentState";
import DevelopmentEnvironmentReducer from "./developmentEnvironmentReducer";
import { Actions } from "./developmentEnvironmentActions";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CreateProviderValue = (props) => {
  const [state, dispatch] = useReducer(DevelopmentEnvironmentReducer, DevelopmentEnvironmentState);

  const toggleMenu = () => {
    dispatch({
      type: Actions.SET_SHOW_MENU,
      payload: !state.showMenu,
    });
  };

  const toggleOneTimeCode = () => {
    dispatch({
      type: Actions.SET_SHOW_ONE_TIME_CODE,
      payload: !state.displayOneTimeCode,
    });
  };

  const toggleLocationState = () => {
    dispatch({
      type: Actions.SET_SHOW_LOCATION_STATE,
      payload: !state.showLocationState,
    });
  };

  const toggleRestart = () => {
    dispatch({
      type: Actions.SET_SHOW_RESTART_TRANSFER,
      payload: !state.displayRestartTransfer,
    });
  };

  const toggleTransferInputReasonOverride = () => {
    if (state.transferInputReasonOverride !== "") {
      dispatch({
        type: Actions.SET_TRANSFER_INPUT_REASON_OVERRIDE,
        payload: "",
      });
    } else {
      dispatch({
        type: Actions.SET_TRANSFER_INPUT_REASON_OVERRIDE,
        payload: TransferActions.linkRothDestination,
      });
    }
  };

  const toggleTransferStatusOverride = () => {
    if (state.transferStatusOveride !== "") {
      dispatch({
        type: Actions.SET_TRANSFER_STATUS_OVERRIDE,
        payload: "",
      });
    } else {
      dispatch({
        type: Actions.SET_TRANSFER_STATUS_OVERRIDE,
        payload: TransferStatusTypes.inProgress,
      });
    }
  };

  const setTransferInputReasonOverride = (reason: TransferActions | "") => {
    dispatch({
      type: Actions.SET_TRANSFER_INPUT_REASON_OVERRIDE,
      payload: reason,
    });
  };

  const setTransferStatusOverride = (status: TransferStatusTypes | "") => {
    dispatch({
      type: Actions.SET_TRANSFER_STATUS_OVERRIDE,
      payload: status,
    });
  };

  const loadState = (localStorageState: (typeof state & Record<string, any>) | null) => {
    if (!localStorageState) return;

    // If a property of the development environment state is removed in the TypeScript file
    // it will not be removed in local storage.
    // This code will remove properties not in the TypeScript state file from local storage
    const payload: typeof state = Object.keys(localStorageState)
      .filter((key) => Object.keys(state).includes(key))
      .reduce((accumulator: typeof state, key: string) => {
        return { ...accumulator, [key]: localStorageState[key] };
      }, {} as typeof state);

    dispatch({
      type: Actions.LOAD_STATE,
      payload,
    });
  };

  return {
    state,
    toggleMenu,
    toggleOneTimeCode,
    loadState,
    toggleRestart,
    toggleTransferInputReasonOverride,
    setTransferInputReasonOverride,
    toggleLocationState,
    toggleTransferStatusOverride,
    setTransferStatusOverride,
  };
};

type DevelopmentEnvironmentValue = ReturnType<typeof CreateProviderValue>;
export const DevelopmentEnvironmentContext = createContext({} as DevelopmentEnvironmentValue);

const DevelopmentEnvironmentProvider: React.FC = (props) => {
  const [hasStateLoaded, setHasStateLoaded] = useState(false);
  const providerValue = CreateProviderValue(props);
  const { loadState } = providerValue;

  useEffect(() => {
    // read state from local storage
    const localStorageState = JSON.parse(localStorage.getItem("devEnvState") as string);
    // dispatch
    loadState(localStorageState);
    setHasStateLoaded(true);
  }, []);

  useEffect(() => {
    if (hasStateLoaded) {
      // save state object to local storage
      localStorage.setItem("devEnvState", JSON.stringify(providerValue.state));
    }
  }, [providerValue.state]);

  return (
    <DevelopmentEnvironmentContext.Provider value={providerValue}>
      {props.children}
    </DevelopmentEnvironmentContext.Provider>
  );
};

export default DevelopmentEnvironmentProvider;
